import removeNonNumber from '@utils/number/remove-non-number';

interface GetNewCursorPositionProps {
  beforeValue: string;
  afterValue: string;
  cursorPosition: number;
  /**
   * @default '.'
   */
  separator?: string;
}

/**
 *
 * This function is used to check if any separator exist on the text
 *
 * @param text
 * @param separator
 * @returns {boolean} true | false
 */
function isFoundSeparator(text: string, separator: string) {
  return text.indexOf(separator) > 0;
}

/**
 *
 * This function is used to count total separator in a text
 *
 * @param text
 * @param separator
 * @returns {number} n
 */
function countTotalSeparator(text: string, separator: string) {
  if (!text) return 0;
  /**
   * Minimum return value is 0
   */
  return (text.split(separator).length || 1) - 1;
}

/**
 *
 * @description this function will return a calculated cursor position for UX purpose after user input
 *
 * @param options
 * @returns {number} cursor_position
 */
export function getNewCursorPosition(options: GetNewCursorPositionProps) {
  const { beforeValue, afterValue, cursorPosition, separator = '.' } = options;
  let newCursor = cursorPosition;

  const valueBeforeCursorPositionForBeforeValue = beforeValue.slice(
    0,
    cursorPosition,
  );
  const valueBeforeCursorPositionForAfterValue = afterValue.slice(
    0,
    cursorPosition,
  );

  const tPlainBeforeValue = removeNonNumber(
    valueBeforeCursorPositionForBeforeValue,
  );
  const tPlainAfterValue = removeNonNumber(
    valueBeforeCursorPositionForAfterValue,
  );
  const isFoundSeparatorForBeforeValue = isFoundSeparator(
    valueBeforeCursorPositionForBeforeValue,
    separator,
  );
  const isFoundSeparatorForAfterValue = isFoundSeparator(
    valueBeforeCursorPositionForAfterValue,
    separator,
  );
  const totalSeparatorInBeforeValue = countTotalSeparator(
    valueBeforeCursorPositionForBeforeValue,
    separator,
  );
  const totalSeparatorInAfterValue = countTotalSeparator(
    valueBeforeCursorPositionForAfterValue,
    separator,
  );

  if (!isFoundSeparatorForBeforeValue && isFoundSeparatorForAfterValue) {
    /**
     * If
     * beforeValue doesn't have cursor but afterValue do have cursor
     * then
     * move cursor to current_position + 1
     *
     * Explanation:
     * This case happen when value move from hundreds to thousands
     * eg. 100 -> 1.100
     */
    newCursor += 1;
  } else if (tPlainBeforeValue.length < tPlainAfterValue.length) {
    /**
     * If
     * plain before value is shorter than plain after value length
     * then
     * move cursor to current_position - 1
     *
     * Explanation:
     * This case happen when user is deleting the character and change value from thousands to hundreds
     * eg. 1.234 -> 134
     */
    newCursor -= 1;
  } else if (
    tPlainAfterValue.length === tPlainBeforeValue.length &&
    afterValue.slice(newCursor - 1, newCursor) === separator
  ) {
    /**
     * Explanation:
     * This case happen when user is adding character after separator
     * eg. 1.234 -> 15.234
     */
    newCursor -= 1;
  }

  if (tPlainAfterValue.length < tPlainBeforeValue.length) {
    /**
     * If
     * plain after value is shorter than plain before value length
     * then
     * continue check
     *
     * Explanation:
     * if plain after value is shorter than plain before value
     * it may cause cursor position is before the character that is inserted
     */
    if (
      afterValue.charAt(newCursor - 1) === separator ||
      afterValue.charAt(newCursor - 1) !==
        tPlainBeforeValue.charAt(tPlainBeforeValue.length - 1) ||
      (totalSeparatorInBeforeValue >= 1 &&
        totalSeparatorInAfterValue > totalSeparatorInBeforeValue)
    ) {
      /**
       * If
       * character before cursor is separator
       * or
       * character before cursor is not the same with last character of tPlainBeforeValue
       * then
       * move cursor position to current_position + 1
       *
       * Explanation:
       * This case happen when cursor is added after first cursor appear (hundreds to millions) and only user insert character after first separator
       * eg. 100.000 -> 1.000.010
       */
      newCursor += 1;
    }
  }

  return newCursor;
}
